import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Tooltip,
    IconButton,
    Chip,
    Divider,
    Button,
    TextField,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Drawer,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import Layout from "../../Layout";
import {
    Edit,
    Close,
    BarChart as BarChartIcon,
    Assessment,
    FileDownload,
    PictureAsPdf,
    GridOn,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    ComposedChart,
    Line,
} from "recharts";
import {
    AGEING_BUCKETS,
    UNSUPPORTED_VALUE,
    classifyAgeingTracker,
    getAgeingBucket,
    getDaysOverdue,
} from "../../utils/trackerNormalization";

const agingBuckets = AGEING_BUCKETS;

const LightGlassCard = styled(Paper)(({ theme }) => ({
    background: "rgba(255,255,255,0.7)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: `1px solid ${alpha("#000", 0.06)}`,
    boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
    borderRadius: 16,
}));

const SectionTitle = styled(Typography)(() => ({
    fontWeight: 700,
    letterSpacing: 0.2,
    color: "#1f2937",
}));

const Subtle = styled(Typography)(() => ({
    color: "#6b7280",
}));

const CountBtn = styled(Button)(({ theme }) => ({
    minWidth: "auto",
    padding: 0,
    textTransform: "none",
    fontWeight: 700,
    textDecoration: "underline",
    color: theme.palette.primary.main,
}));

const StickyHeaderCell = styled(TableCell)(() => ({
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "rgba(250,250,250,0.95)",
    backdropFilter: "blur(6px)",
    fontWeight: 700,
}));

const StickyFirstCol = styled(TableCell)(() => ({
    position: "sticky",
    left: 0,
    zIndex: 1,
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(6px)",
    fontWeight: 600,
}));

const formatDateInIST = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));
    return `${values.year}-${values.month}-${values.day}`;
};

const formatDateTimeInIST = (value = new Date()) =>
    new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(value);

const sanitizeSheetName = (name) =>
    String(name || "Law Area")
        .replace(/[/\\?*[\]:]/g, " ")
        .trim()
        .slice(0, 31) || "Law Area";

const buildUniqueSheetNames = (lawAreas, reservedNames) => {
    const used = new Set(reservedNames.map((name) => name.toLowerCase()));
    return lawAreas.reduce((sheetNames, lawArea) => {
        const base = sanitizeSheetName(lawArea);
        let candidate = base;
        let suffix = 2;
        while (used.has(candidate.toLowerCase())) {
            const postfix = ` (${suffix})`;
            candidate = `${base.slice(0, 31 - postfix.length).trim()}${postfix}`;
            suffix += 1;
        }
        used.add(candidate.toLowerCase());
        sheetNames[lawArea] = candidate;
        return sheetNames;
    }, {});
};

const ComplianceAgingReport = () => {
    const [companyName, setCompanyName] = useState("Company Name");
    const [reportPeriod, setReportPeriod] = useState({ upto: "YYYY-MM-DD" });
    const [processedData, setProcessedData] = useState({});
    const [selectedTrackers, setSelectedTrackers] = useState([]);
    const [trackerHeading, setTrackerHeading] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [mainChartsDrawerOpen, setMainChartsDrawerOpen] = useState(false);
    const [lawAreaChartsDrawer, setLawAreaChartsDrawer] = useState(null); // law area name or null
    const [exportAnchor, setExportAnchor] = useState(null);

    const location = useLocation();
    const { state } = location;
    const reportData = state?.reportData?.groupedTrackers || {};
    const navigate = useNavigate();

    useEffect(() => {
        if (state) {
            setCompanyName(state?.Company || "Company Name");
            setReportPeriod({ upto: state?.enddate || "YYYY-MM-DD" });
        }
    }, [state]);

    const handleEdit = async (uin) => navigate(`/Edit/${uin}`);

    useEffect(() => {
        if (Object.keys(reportData).length > 0) {
            const result = {};

            Object.keys(reportData).forEach((locationKey) => {
                const lawAreas = reportData[locationKey];

                Object.keys(lawAreas).forEach((lawArea) => {
                    const trackers = lawAreas[lawArea];
                    if (!Array.isArray(trackers)) return;

                    if (!result[lawArea]) {
                        result[lawArea] = {
                            agingData: {},
                            totals: {
                                openHigh: 0,
                                openMedium: 0,
                                openLow: 0,
                                inProcessHigh: 0,
                                inProcessMedium: 0,
                                inProcessLow: 0,
                                cannotComply: 0,
                                notComplied: 0,
                                [UNSUPPORTED_VALUE]: 0,
                            },
                        };
                        agingBuckets.forEach((b) => {
                            result[lawArea].agingData[b.label] = {
                                openHigh: [],
                                openMedium: [],
                                openLow: [],
                                inProcessHigh: [],
                                inProcessMedium: [],
                                inProcessLow: [],
                                cannotComply: [],
                                notComplied: [],
                                [UNSUPPORTED_VALUE]: [],
                            };
                        });
                    }

                    trackers.forEach((tracker) => {
                        const { bucket, category } = classifyAgeingTracker(tracker, state?.enddate);
                        result[lawArea].agingData[bucket][category].push(tracker);
                        result[lawArea].totals[category]++;
                    });
                });
            });

            setProcessedData(result);
        }
    }, [reportData, state?.enddate]);

    // ─── Chart data (derived from processedData — same logic as tables) ───
    const lawAreaChartData = useMemo(() => {
        if (!processedData || Object.keys(processedData).length === 0) return [];
        return Object.keys(processedData).map((lawArea) => {
            const t = processedData[lawArea].totals;
            const total =
                (t?.openHigh ?? 0) +
                (t?.openMedium ?? 0) +
                (t?.openLow ?? 0) +
                (t?.inProcessHigh ?? 0) +
                (t?.inProcessMedium ?? 0) +
                (t?.inProcessLow ?? 0) +
                (t?.cannotComply ?? 0) +
                (t?.notComplied ?? 0) +
                (t?.[UNSUPPORTED_VALUE] ?? 0);
            return { name: lawArea, total, lawArea };
        });
    }, [processedData]);

    const agingBucketChartData = useMemo(() => {
        if (!processedData || Object.keys(processedData).length === 0) return [];
        return agingBuckets.map((bucket) => {
            let total = 0;
            Object.keys(processedData).forEach((lawArea) => {
                const row = processedData[lawArea].agingData?.[bucket.label];
                if (!row) return;
                total +=
                    row.openHigh.length +
                    row.openMedium.length +
                    row.openLow.length +
                    row.inProcessHigh.length +
                    row.inProcessMedium.length +
                    row.inProcessLow.length +
                    row.cannotComply.length +
                    row.notComplied.length +
                    row[UNSUPPORTED_VALUE].length;
            });
            return { bucket: bucket.label, total, days: bucket.label };
        });
    }, [processedData]);

    const agingBucketStackedData = useMemo(() => {
        if (!processedData || Object.keys(processedData).length === 0) return [];
        return agingBuckets.map((bucket) => {
            const point = { bucket: bucket.label };
            let openHigh = 0,
                openMedium = 0,
                openLow = 0,
                inProcessHigh = 0,
                inProcessMedium = 0,
                inProcessLow = 0,
                cannotComply = 0,
                notComplied = 0,
                unsupported = 0;
            Object.keys(processedData).forEach((lawArea) => {
                const row = processedData[lawArea].agingData?.[bucket.label];
                if (!row) return;
                openHigh += row.openHigh.length;
                openMedium += row.openMedium.length;
                openLow += row.openLow.length;
                inProcessHigh += row.inProcessHigh.length;
                inProcessMedium += row.inProcessMedium.length;
                inProcessLow += row.inProcessLow.length;
                cannotComply += row.cannotComply.length;
                notComplied += row.notComplied.length;
                unsupported += row[UNSUPPORTED_VALUE].length;
            });
            point.openHigh = openHigh;
            point.openMedium = openMedium;
            point.openLow = openLow;
            point.inProcessHigh = inProcessHigh;
            point.inProcessMedium = inProcessMedium;
            point.inProcessLow = inProcessLow;
            point.cannotComply = cannotComply;
            point.notComplied = notComplied;
            point.unsupported = unsupported;
            point.total =
                openHigh +
                openMedium +
                openLow +
                inProcessHigh +
                inProcessMedium +
                inProcessLow +
                cannotComply +
                notComplied +
                unsupported;
            return point;
        });
    }, [processedData]);

    const statusPieData = useMemo(() => {
        if (!processedData || Object.keys(processedData).length === 0) return [];
        let openHigh = 0,
            openMedium = 0,
            openLow = 0,
            inProcessHigh = 0,
            inProcessMedium = 0,
            inProcessLow = 0,
            cannotComply = 0,
            notComplied = 0,
            unsupported = 0;
        Object.keys(processedData).forEach((lawArea) => {
            const t = processedData[lawArea].totals;
            openHigh += t?.openHigh ?? 0;
            openMedium += t?.openMedium ?? 0;
            openLow += t?.openLow ?? 0;
            inProcessHigh += t?.inProcessHigh ?? 0;
            inProcessMedium += t?.inProcessMedium ?? 0;
            inProcessLow += t?.inProcessLow ?? 0;
            cannotComply += t?.cannotComply ?? 0;
            notComplied += t?.notComplied ?? 0;
            unsupported += t?.[UNSUPPORTED_VALUE] ?? 0;
        });
        const items = [
            { name: "Open (High)", value: openHigh, fill: "#c62828" },
            { name: "Open (Medium)", value: openMedium, fill: "#ef6c00" },
            { name: "Open (Low)", value: openLow, fill: "#2e7d32" },
            { name: "In Process (High)", value: inProcessHigh, fill: "#1565c0" },
            { name: "In Process (Medium)", value: inProcessMedium, fill: "#0277bd" },
            { name: "In Process (Low)", value: inProcessLow, fill: "#0288d1" },
            { name: "Cannot Comply", value: cannotComply, fill: "#6a1b9a" },
            { name: "Not Complied", value: notComplied, fill: "#ad1457" },
            { name: UNSUPPORTED_VALUE, value: unsupported, fill: "#616161" },
        ];
        return items.filter((d) => d.value > 0);
    }, [processedData]);

    // Per–law area chart data for the section drawers (same logic as table rows for that law area)
    const getLawAreaChartData = useMemo(() => {
        return (lawArea) => {
            if (!processedData?.[lawArea]) return { bucketData: [], statusPieData: [] };
            const t = processedData[lawArea].totals;
            const bucketData = agingBuckets.map((bucket) => {
                const row = processedData[lawArea].agingData?.[bucket.label];
                if (!row) return { bucket: bucket.label, total: 0 };
                const total =
                    row.openHigh.length +
                    row.openMedium.length +
                    row.openLow.length +
                    row.inProcessHigh.length +
                    row.inProcessMedium.length +
                    row.inProcessLow.length +
                    row.cannotComply.length +
                    row.notComplied.length +
                    row[UNSUPPORTED_VALUE].length;
                return { bucket: bucket.label, total };
            });
            const statusItems = [
                { name: "Open (High)", value: t?.openHigh ?? 0, fill: "#c62828" },
                { name: "Open (Medium)", value: t?.openMedium ?? 0, fill: "#ef6c00" },
                { name: "Open (Low)", value: t?.openLow ?? 0, fill: "#2e7d32" },
                { name: "In Process (High)", value: t?.inProcessHigh ?? 0, fill: "#1565c0" },
                { name: "In Process (Medium)", value: t?.inProcessMedium ?? 0, fill: "#0277bd" },
                { name: "In Process (Low)", value: t?.inProcessLow ?? 0, fill: "#0288d1" },
                { name: "Cannot Comply", value: t?.cannotComply ?? 0, fill: "#6a1b9a" },
                { name: "Not Complied", value: t?.notComplied ?? 0, fill: "#ad1457" },
                { name: UNSUPPORTED_VALUE, value: t?.[UNSUPPORTED_VALUE] ?? 0, fill: "#616161" },
            ];
            const statusPieData = statusItems.filter((d) => d.value > 0);
            return { bucketData, statusPieData };
        };
    }, [processedData]);

    const openModalWith = (trackers, heading) => {
        setSelectedTrackers(trackers);
        setTrackerHeading(heading);
        setQuery("");
        setPage(0);
        setModalOpen(true);
    };

    const handleTrackerClick = (trackers, columnName, status, lawArea, ageBucket, risk) => {
        const heading = `${columnName} ${status} in ${lawArea} pending from ${ageBucket} days${risk ? ` • Risk: ${risk}` : ""
            }`;
        openModalWith(trackers, heading);
    };

    const { lawAreas, trackersByLawArea } = useMemo(() => {
        const groups = {};
        Object.values(reportData).forEach((lawAreasByLocation) => {
            Object.entries(lawAreasByLocation || {}).forEach(([lawArea, trackers]) => {
                const key = String(lawArea || "Unspecified");
                if (!groups[key]) groups[key] = [];
                if (Array.isArray(trackers)) groups[key].push(...trackers);
            });
        });

        const orderedLawAreas = Object.keys(groups).sort((a, b) => a.localeCompare(b));
        return { lawAreas: orderedLawAreas, trackersByLawArea: groups };
    }, [reportData]);

    const getAgeingDetails = (tracker) => {
        const daysOverdue = getDaysOverdue(tracker?.dueDate, state?.enddate);
        if (daysOverdue == null) {
            return { daysOverdue: "", bucket: "Unavailable" };
        }
        return {
            daysOverdue,
            bucket: getAgeingBucket(daysOverdue),
        };
    };

    const formatPeople = (people) =>
        Array.isArray(people) && people.length
            ? people
                  .filter(Boolean)
                  .map((person) =>
                      [person.name, person.role ? `(${person.role})` : ""].filter(Boolean).join(" ")
                  )
                  .join("; ")
            : "";

    const buildExportRows = (lawArea) =>
        (trackersByLawArea[lawArea] || []).map((tracker) => {
            const { daysOverdue, bucket } = getAgeingDetails(tracker);
            return {
                UIN: tracker.uniqueIdentifier || "",
                Company: companyName || "",
                Location: tracker.location || "",
                "Law Area": tracker.lawArea || lawArea,
                "Compliance Activity": tracker.complianceActivity || "",
                Description: tracker.description || "",
                Category: Array.isArray(tracker.category) ? tracker.category.join(", ") : tracker.category || "",
                "Act or Rule": tracker.actOrRule || "",
                Periodicity: tracker.periodicity || "",
                Consequence: tracker.consequence || "",
                "Source URL": tracker.sourceURL || "",
                "Due Date (IST)": formatDateInIST(tracker.dueDate),
                "Completion Date (IST)": formatDateInIST(tracker.completionDate),
                "Days Overdue": daysOverdue,
                "Ageing Bucket": bucket,
                Risk: tracker.risk || "",
                Status: tracker.status || "",
                "Proof Submitted": tracker.proof ? "Yes" : "No",
                "Proof Not Required": tracker.proofNotRequired ? "Yes" : "No",
                "Tracker Owner(s)": formatPeople(tracker.trackerOwner),
                "Tracker Manager(s)": formatPeople(tracker.trackerManager),
                "Legal Update Link": tracker.legalUpdateLink || "",
            };
        });

    const makeExportFilename = (extension) => {
        const safeCompany = String(companyName || "company")
            .replace(/[^\w- ]/g, "")
            .trim()
            .replace(/\s+/g, "_");
        const asOf = reportPeriod.upto || "as_of_date";
        return `ageing_report_${safeCompany}_${asOf}_${extension === "xlsx" ? "excel" : "pdf"}.${extension}`;
    };

    const exportExcel = () => {
        if (!lawAreas.length) return;

        const workbook = XLSX.utils.book_new();
        const sheetNames = buildUniqueSheetNames(lawAreas, ["Summary", "Export Meta"]);
        const summaryRows = lawAreas.map((lawArea) => ({
            "Law Area": lawArea,
            "Trackers Returned": (trackersByLawArea[lawArea] || []).length,
        }));

        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), "Summary");
        XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.aoa_to_sheet([
                ["Company", companyName || ""],
                ["As-of Date (IST)", reportPeriod.upto || ""],
                ["Locations", Array.isArray(state?.Location) ? state.Location.join(", ") : state?.Location || "All"],
                ["Generated At (IST)", formatDateTimeInIST()],
                ["Scope", "All trackers returned by the Ageing Report API (status is not complied; due date is on or before the as-of date)"],
            ]),
            "Export Meta"
        );

        lawAreas.forEach((lawArea) => {
            const rows = buildExportRows(lawArea);
            const worksheet = XLSX.utils.json_to_sheet(rows);
            worksheet["!cols"] = [
                { wch: 16 }, { wch: 24 }, { wch: 16 }, { wch: 16 }, { wch: 30 },
                { wch: 48 }, { wch: 20 }, { wch: 30 }, { wch: 16 }, { wch: 28 },
                { wch: 40 }, { wch: 16 }, { wch: 20 }, { wch: 14 }, { wch: 20 },
                { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 28 }, { wch: 28 }, { wch: 52 },
            ];
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetNames[lawArea]);
        });

        XLSX.writeFile(workbook, makeExportFilename("xlsx"));
    };

    const exportPdf = () => {
        if (!lawAreas.length) return;

        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const pageHeight = doc.internal.pageSize.getHeight();
        const columns = [
            "UIN",
            "Location",
            "Activity",
            "Tracker Details",
            "Due Date (IST)",
            "Status / Risk",
            "Owners / Managers",
            "Legal Update Link",
        ];

        lawAreas.forEach((lawArea, index) => {
            if (index > 0) doc.addPage();

            const rows = buildExportRows(lawArea);
            doc.setFontSize(15);
            doc.text("Compliance Ageing Report", 36, 36);
            doc.setFontSize(11);
            doc.text(`Law Area: ${lawArea}`, 36, 54);
            doc.setFontSize(9);
            doc.text(`Company: ${companyName || "-"}`, 36, 69);
            doc.text(`As of: ${reportPeriod.upto || "-"} (IST)`, 36, 82);
            doc.text(
                `Locations: ${Array.isArray(state?.Location) ? state.Location.join(", ") : state?.Location || "All"}`,
                36,
                95
            );

            autoTable(doc, {
                startY: 110,
                head: [columns],
                body: rows.map((row) => [
                    row.UIN,
                    row.Location,
                    row["Compliance Activity"],
                    [
                        `Description: ${row.Description || "-"}`,
                        `Category: ${row.Category || "-"}`,
                        `Act/Rule: ${row["Act or Rule"] || "-"}`,
                        `Periodicity: ${row.Periodicity || "-"}`,
                        `Consequence: ${row.Consequence || "-"}`,
                        `Source: ${row["Source URL"] || "-"}`,
                    ].join("\n"),
                    row["Due Date (IST)"],
                    [
                        `Status: ${row.Status || "-"}`,
                        `Risk: ${row.Risk || "-"}`,
                        `Age: ${row["Days Overdue"]} (${row["Ageing Bucket"]})`,
                        `Proof submitted: ${row["Proof Submitted"]}`,
                        `Proof not required: ${row["Proof Not Required"]}`,
                    ].join("\n"),
                    [`Owners: ${row["Tracker Owner(s)"] || "-"}`, `Managers: ${row["Tracker Manager(s)"] || "-"}`].join("\n"),
                    row["Legal Update Link"],
                ]),
                margin: { left: 36, right: 36, bottom: 28 },
                styles: { fontSize: 5.5, cellPadding: 2, overflow: "linebreak", valign: "top" },
                headStyles: { fillColor: [25, 118, 210], fontSize: 6, halign: "center" },
                columnStyles: {
                    0: { cellWidth: 42 },
                    1: { cellWidth: 44 },
                    2: { cellWidth: 68 },
                    3: { cellWidth: 170 },
                    4: { cellWidth: 50 },
                    5: { cellWidth: 74 },
                    6: { cellWidth: 100 },
                    7: { cellWidth: 175 },
                },
                didDrawPage: () => {
                    doc.setFontSize(8);
                    doc.text(
                        `Generated ${formatDateTimeInIST()} IST — Page ${doc.internal.getNumberOfPages()}`,
                        36,
                        pageHeight - 12
                    );
                },
            });
        });

        doc.save(makeExportFilename("pdf"));
    };

    const borderColor = alpha("#000", 0.08);
    const headBg = "#fafafa";

    const renderTables = (data) =>
        Object.keys(data).map((lawArea, index) => (
            <LightGlassCard key={index} elevation={0} sx={{ p: 3, mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
                    <SectionTitle variant="h6" sx={{ textAlign: "center" }}>
                        {lawArea} — Compliance Aging
                    </SectionTitle>
                    <Tooltip title="Charts for this law area" arrow>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setLawAreaChartsDrawer(lawArea)}
                            sx={{ mt: -0.5 }}
                        >
                            <Assessment fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Subtle variant="body2" sx={{ textAlign: "center", mb: 2 }}>
                    Click counts to view details
                </Subtle>

                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        overflow: "auto",
                        border: `1px solid ${borderColor}`,
                        maxHeight: 520,
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <StickyHeaderCell rowSpan={2} sx={{ borderColor, background: headBg }}>
                                    Aging Days
                                </StickyHeaderCell>
                                <StickyHeaderCell colSpan={3} align="center" sx={{ borderColor, background: headBg }}>
                                    Open
                                </StickyHeaderCell>
                                <StickyHeaderCell colSpan={3} align="center" sx={{ borderColor, background: headBg }}>
                                    In Process
                                </StickyHeaderCell>
                                <StickyHeaderCell rowSpan={2} align="center" sx={{ borderColor, background: headBg }}>
                                    Cannot Comply
                                </StickyHeaderCell>
                                <StickyHeaderCell rowSpan={2} align="center" sx={{ borderColor, background: headBg }}>
                                    Not Complied
                                </StickyHeaderCell>
                                <StickyHeaderCell rowSpan={2} align="center" sx={{ borderColor, background: headBg }}>
                                    Other / Unsupported
                                </StickyHeaderCell>
                                <StickyHeaderCell rowSpan={2} align="center" sx={{ borderColor, background: headBg }}>
                                    Total
                                </StickyHeaderCell>
                            </TableRow>
                            <TableRow>
                                {["High", "Medium", "Low", "High", "Medium", "Low"].map((label, i) => (
                                    <StickyHeaderCell key={i} align="center" sx={{ borderColor, background: headBg }}>
                                        {label}
                                    </StickyHeaderCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody
                            sx={{
                                "& .MuiTableCell-root": { borderColor },
                                "& tr:nth-of-type(even)": { backgroundColor: "#fdfdfd" },
                            }}
                        >
                            {agingBuckets.map((bucket) => {
                                const row = data[lawArea].agingData[bucket.label];
                                const total =
                                    row.openHigh.length +
                                    row.openMedium.length +
                                    row.openLow.length +
                                    row.inProcessHigh.length +
                                    row.inProcessMedium.length +
                                    row.inProcessLow.length +
                                    row.cannotComply.length +
                                    row.notComplied.length +
                                    row[UNSUPPORTED_VALUE].length;

                                const cell = (arr, colName, status, risk) =>
                                    arr.length > 0 ? (
                                        <CountBtn
                                            onClick={() => handleTrackerClick(arr, colName, status, lawArea, bucket.label, risk || "")}
                                        >
                                            {arr.length}
                                        </CountBtn>
                                    ) : (
                                        0
                                    );

                                return (
                                    <TableRow key={bucket.label} hover>
                                        <StickyFirstCol>{bucket.label}</StickyFirstCol>
                                        <TableCell align="center">{cell(row.openHigh, "Open", "High", "High")}</TableCell>
                                        <TableCell align="center">{cell(row.openMedium, "Open", "Medium", "Medium")}</TableCell>
                                        <TableCell align="center">{cell(row.openLow, "Open", "Low", "Low")}</TableCell>
                                        <TableCell align="center">
                                            {cell(row.inProcessHigh, "In Process", "High", "High")}
                                        </TableCell>
                                        <TableCell align="center">
                                            {cell(row.inProcessMedium, "In Process", "Medium", "Medium")}
                                        </TableCell>
                                        <TableCell align="center">{cell(row.inProcessLow, "In Process", "Low", "Low")}</TableCell>
                                        <TableCell align="center">{cell(row.cannotComply, "Cannot Comply", "", "")}</TableCell>
                                        <TableCell align="center">{cell(row.notComplied, "Not Complied", "", "")}</TableCell>
                                        <TableCell align="center">
                                            {cell(row[UNSUPPORTED_VALUE], UNSUPPORTED_VALUE, "", "")}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            {total}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {/* Final Total Row */}
                            <TableRow>
                                <StickyFirstCol sx={{ fontWeight: 800 }}>Final Total</StickyFirstCol>
                                {[
                                    "openHigh",
                                    "openMedium",
                                    "openLow",
                                    "inProcessHigh",
                                    "inProcessMedium",
                                    "inProcessLow",
                                    "cannotComply",
                                    "notComplied",
                                    UNSUPPORTED_VALUE,
                                ].map((key) => (
                                    <TableCell key={key} align="center" sx={{ fontWeight: 700 }}>
                                        {agingBuckets.reduce((sum, b) => sum + data[lawArea].agingData[b.label][key].length, 0)}
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{ fontWeight: 800 }}>
                                    {agingBuckets.reduce((sum, b) => {
                                        const r = data[lawArea].agingData[b.label];
                                        return (
                                            sum +
                                            r.openHigh.length +
                                            r.openMedium.length +
                                            r.openLow.length +
                                            r.inProcessHigh.length +
                                            r.inProcessMedium.length +
                                            r.inProcessLow.length +
                                            r.cannotComply.length +
                                            r.notComplied.length +
                                            r[UNSUPPORTED_VALUE].length
                                        );
                                    }, 0)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </LightGlassCard>
        ));

    const riskChip = (risk) => {
        const map = {
            High: { color: "error", label: "High" },
            Medium: { color: "warning", label: "Medium" },
            Low: { color: "success", label: "Low" },
        };
        const cfg = map[risk] || { color: "default", label: risk || "-" };
        return <Chip size="small" label={cfg.label} color={cfg.color} variant="filled" />;
    };

    const statusChip = (status) => {
        const map = {
            open: { color: "primary", label: "Open" },
            "in-process": { color: "info", label: "In Process" },
            "cannot-comply": { color: "secondary", label: "Cannot Comply" },
            "not-complied": { color: "error", label: "Not Complied" },
        };
        const cfg = map[status] || { color: "default", label: status || "-" };
        return <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />;
    };

    // Modal filtering + pagination
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return selectedTrackers;
        return selectedTrackers.filter((t) => {
            const fields = [
                t.uniqueIdentifier,
                t.location,
                t.complianceActivity,
                t.lawArea,
                t.actOrRule,
                t.risk,
                t.status,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return fields.includes(q);
        });
    }, [query, selectedTrackers]);

    const paged = useMemo(() => {
        const start = page * rowsPerPage;
        return filtered.slice(start, start + rowsPerPage);
    }, [filtered, page, rowsPerPage]);

    const onChangePage = (_, newPage) => setPage(newPage);
    const onChangeRpp = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    return (
        <Layout title="Ageing Report">
            <Box
                sx={{
                    minHeight: "100vh",
                    background: "linear-gradient(180deg, #f5f7fb 0%, #ffffff 40%, #f7fafc 100%)",
                    py: { xs: 3, md: 6 },
                }}
            >
                <Container maxWidth="lg">
                    <LightGlassCard elevation={0} sx={{ p: { xs: 2.5, md: 4 }, mb: 4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                            <SectionTitle variant="h5" component="h1" sx={{ textAlign: "center" }}>
                                {companyName}
                            </SectionTitle>
                            {Object.keys(processedData).length > 0 && (
                                <Tooltip title="Open overview charts" arrow>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<BarChartIcon />}
                                        onClick={() => setMainChartsDrawerOpen(true)}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Charts
                                    </Button>
                                </Tooltip>
                            )}
                            {lawAreas.length > 0 && (
                                <>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<FileDownload />}
                                        onClick={(event) => setExportAnchor(event.currentTarget)}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Export
                                    </Button>
                                    <Menu
                                        anchorEl={exportAnchor}
                                        open={Boolean(exportAnchor)}
                                        onClose={() => setExportAnchor(null)}
                                    >
                                        <MenuItem
                                            onClick={() => {
                                                exportExcel();
                                                setExportAnchor(null);
                                            }}
                                        >
                                            <ListItemIcon>
                                                <GridOn fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>Excel (.xlsx) — Law Area sheets</ListItemText>
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => {
                                                exportPdf();
                                                setExportAnchor(null);
                                            }}
                                        >
                                            <ListItemIcon>
                                                <PictureAsPdf fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>PDF — Law Area sections</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                        </Box>
                        <Subtle variant="subtitle1" component="h2" sx={{ textAlign: "center", mb: 0.5 }}>
                            Compliance Aging Report up to {reportPeriod.upto}
                        </Subtle>
                        <Subtle variant="subtitle2" component="h3" sx={{ textAlign: "center", mb: 0 }}>
                            Location: {Array.isArray(state?.Location) ? state.Location.join(", ") : state?.Location || "All"}
                        </Subtle>
                    </LightGlassCard>

                    {Object.keys(processedData).length > 0 ? (
                        renderTables(processedData)
                    ) : (
                        <LightGlassCard elevation={0} sx={{ p: 3 }}>
                            <Typography variant="body1" sx={{ textAlign: "center", color: "#6b7280" }}>
                                No data available for the selected filters.
                            </Typography>
                        </LightGlassCard>
                    )}
                </Container>
            </Box>

            {/* Main charts drawer — overview, does not cover full page */}
            <Drawer
                anchor="right"
                open={mainChartsDrawerOpen}
                onClose={() => setMainChartsDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 420 },
                        maxWidth: 420,
                        borderLeft: `1px solid ${alpha("#000", 0.08)}`,
                        boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                    },
                }}
            >
                <Box sx={{ p: 2, borderBottom: `1px solid ${alpha("#000", 0.08)}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <SectionTitle variant="subtitle1">Overview charts</SectionTitle>
                    <IconButton size="small" onClick={() => setMainChartsDrawerOpen(false)}>
                        <Close />
                    </IconButton>
                </Box>
                <Box sx={{ p: 2, overflow: "auto", "& > *": { mb: 3 } }}>
                    <Box sx={{ height: 260 }}>
                        <Subtle variant="overline" sx={{ display: "block", mb: 0.5 }}>Trackers by law area</Subtle>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={lawAreaChartData} margin={{ top: 8, right: 8, left: 0, bottom: 20 }} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha("#000", 0.06)} />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                                <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 10 }} tickFormatter={(v) => (v?.length > 10 ? v.slice(0, 8) + "…" : v)} />
                                <RechartsTooltip formatter={(v) => [v, "Trackers"]} contentStyle={{ borderRadius: 8, border: `1px solid ${alpha("#000", 0.08)}` }} />
                                <Bar name="Total" dataKey="total" fill="#1976d2" radius={[0, 4, 4, 0]} maxBarSize={24} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box sx={{ height: 240 }}>
                        <Subtle variant="overline" sx={{ display: "block", mb: 0.5 }}>Status & risk</Subtle>
                        {statusPieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="45%" outerRadius="75%" paddingAngle={2} stroke={alpha("#fff", 0.8)} strokeWidth={1}>
                                        {statusPieData.map((entry, i) => (
                                            <Cell key={i} fill={entry.fill} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip formatter={(v) => [v, "Trackers"]} contentStyle={{ borderRadius: 8, border: `1px solid ${alpha("#000", 0.08)}` }} />
                                    <Legend wrapperStyle={{ fontSize: 10 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No status data</Typography>
                        )}
                    </Box>
                    <Box sx={{ height: 240 }}>
                        <Subtle variant="overline" sx={{ display: "block", mb: 0.5 }}>Aging buckets (stacked)</Subtle>
                        <ResponsiveContainer width="100%" height={200}>
                            <ComposedChart data={agingBucketStackedData} margin={{ top: 8, right: 8, left: 0, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha("#000", 0.06)} />
                                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                <RechartsTooltip contentStyle={{ borderRadius: 8, border: `1px solid ${alpha("#000", 0.08)}` }} />
                                <Legend wrapperStyle={{ fontSize: 9 }} />
                                <Bar dataKey="openHigh" name="Open H" stackId="a" fill="#c62828" />
                                <Bar dataKey="openMedium" name="Open M" stackId="a" fill="#ef6c00" />
                                <Bar dataKey="openLow" name="Open L" stackId="a" fill="#2e7d32" />
                                <Bar dataKey="inProcessHigh" name="InProc H" stackId="a" fill="#1565c0" />
                                <Bar dataKey="inProcessMedium" name="InProc M" stackId="a" fill="#0277bd" />
                                <Bar dataKey="inProcessLow" name="InProc L" stackId="a" fill="#0288d1" />
                                <Bar dataKey="cannotComply" name="Cannot" stackId="a" fill="#6a1b9a" />
                                <Bar dataKey="notComplied" name="Not Compl" stackId="a" fill="#ad1457" />
                                <Bar dataKey="unsupported" name="Other" stackId="a" fill="#616161" />
                                <Line type="monotone" dataKey="total" name="Total" stroke="#1f2937" strokeWidth={2} dot={{ r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box sx={{ height: 220 }}>
                        <Subtle variant="overline" sx={{ display: "block", mb: 0.5 }}>Total per bucket</Subtle>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={agingBucketChartData} margin={{ top: 8, right: 8, left: 0, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha("#000", 0.06)} />
                                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                <RechartsTooltip formatter={(v) => [v, "Trackers"]} contentStyle={{ borderRadius: 8, border: `1px solid ${alpha("#000", 0.08)}` }} />
                                <Bar name="Trackers" dataKey="total" fill="#5c6bc0" radius={[4, 4, 0, 0]} maxBarSize={32} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            </Drawer>

            {/* Per–law area charts drawer */}
            <Drawer
                anchor="right"
                open={Boolean(lawAreaChartsDrawer)}
                onClose={() => setLawAreaChartsDrawer(null)}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 380 },
                        maxWidth: 380,
                        borderLeft: `1px solid ${alpha("#000", 0.08)}`,
                        boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                    },
                }}
            >
                {lawAreaChartsDrawer && (() => {
                    const { bucketData, statusPieData: laStatusPie } = getLawAreaChartData(lawAreaChartsDrawer);
                    return (
                        <>
                            <Box sx={{ p: 2, borderBottom: `1px solid ${alpha("#000", 0.08)}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <SectionTitle variant="subtitle1">{lawAreaChartsDrawer} — Charts</SectionTitle>
                                <IconButton size="small" onClick={() => setLawAreaChartsDrawer(null)}>
                                    <Close />
                                </IconButton>
                            </Box>
                            <Box sx={{ p: 2, overflow: "auto", "& > *": { mb: 3 } }}>
                                <Box sx={{ height: 220 }}>
                                    <Subtle variant="overline" sx={{ display: "block", mb: 0.5 }}>Aging buckets (days)</Subtle>
                                    <ResponsiveContainer width="100%" height={180}>
                                        <BarChart data={bucketData} margin={{ top: 8, right: 8, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={alpha("#000", 0.06)} />
                                            <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                            <RechartsTooltip formatter={(v) => [v, "Trackers"]} contentStyle={{ borderRadius: 8, border: `1px solid ${alpha("#000", 0.08)}` }} />
                                            <Bar name="Trackers" dataKey="total" fill="#1976d2" radius={[4, 4, 0, 0]} maxBarSize={36} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Box sx={{ height: 240 }}>
                                    <Subtle variant="overline" sx={{ display: "block", mb: 0.5 }}>Status & risk</Subtle>
                                    {laStatusPie.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={200}>
                                            <PieChart>
                                                <Pie data={laStatusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="45%" outerRadius="75%" paddingAngle={2} stroke={alpha("#fff", 0.8)} strokeWidth={1}>
                                                    {laStatusPie.map((entry, i) => (
                                                        <Cell key={i} fill={entry.fill} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip formatter={(v) => [v, "Trackers"]} contentStyle={{ borderRadius: 8, border: `1px solid ${alpha("#000", 0.08)}` }} />
                                                <Legend wrapperStyle={{ fontSize: 10 }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No status data for this law area</Typography>
                                    )}
                                </Box>
                            </Box>
                        </>
                    );
                })()}
            </Drawer>

            {/* Centered Modal (Dialog) */}
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth="lg"
                aria-labelledby="trackers-dialog-title"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: "rgba(255,255,255,0.92)",
                        backdropFilter: "blur(12px)",
                        border: `1px solid ${alpha("#000", 0.06)}`,
                    },
                }}
            >
                <DialogTitle
                    id="trackers-dialog-title"
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {trackerHeading || "Details"}
                    </Typography>
                    <IconButton onClick={() => setModalOpen(false)}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ p: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search UIN, activity, law, location, status…"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPage(0);
                            }}
                            autoFocus
                        />
                    </Box>

                    <Box sx={{ px: 2, pb: 2 }}>
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                overflow: "auto",
                                border: `1px solid ${alpha("#000", 0.08)}`,
                                maxHeight: "60vh",
                            }}
                        >
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {[
                                            "UIN",
                                            "Location",
                                            "Compliance Activity",
                                            "Law Area",
                                            "Act or Rule",
                                            "Due Date",
                                            "Risk",
                                            "Status",
                                            "Actions",
                                        ].map((h) => (
                                            <StickyHeaderCell key={h} sx={{ background: "#fafafa" }}>
                                                {h}
                                            </StickyHeaderCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paged.map((tracker, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell sx={{ fontWeight: 600 }}>{tracker.uniqueIdentifier}</TableCell>
                                            <TableCell>{tracker.location}</TableCell>
                                            <TableCell>{tracker.complianceActivity}</TableCell>
                                            <TableCell>{tracker.lawArea}</TableCell>
                                            <TableCell>{tracker.actOrRule}</TableCell>
                                            <TableCell>
                                                {formatDateInIST(tracker.dueDate) || "-"}
                                            </TableCell>
                                            <TableCell>{riskChip(tracker.risk)}</TableCell>
                                            <TableCell>{statusChip(tracker.status)}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit Detail" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEdit(tracker.uniqueIdentifier)}
                                                        color="primary"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {paged.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 6, color: "#6b7280" }}>
                                                No results
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 2 }}>
                    <TablePagination
                        component="div"
                        count={filtered.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ ml: "auto" }}
                    />
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default ComplianceAgingReport;

